import { collection } from '@/infra/collection'
import {
  DeclineOpportunityOfferRequest,
  GetOpportunityOfferRequest,
  ListOpportunityOffersRequest,
  type OpportunityOffer as OpportunityOfferProto,
} from '@taxfyle/api-internal/internal/opportunity_pb'
import {
  OpportunityOffer,
  type OpportunityOfferProps,
} from './OpportunityOffer'
import {
  mapExpirationActionProtoToOfferExpirationAction,
  mapOpportunityOfferConsultationProtoToConsultation,
} from './opportunity-offer-mappers'
import { type OpportunitiesApi } from '@/features/opportunities/api/opportunities-api'
import type { PrimeUserFn, PrimeWorkspaceFn } from '@/features/iam'
import { Id } from '@/infra/id'
import type { AcceptOfferFn } from '@/features/jobs'
import {
  catchError,
  defer,
  EMPTY,
  merge,
  mergeMap,
  switchMap,
  type Observable,
} from 'rxjs'
import { filterNonNil, whileActive } from '@/utils/rx-util'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { filter, find, map, share } from 'rxjs/operators'
import { timestampToDate, maybeDecimalToNumber } from '@/utils/grpc-util'
import TypeCase = RealtimeEvent.TypeCase

/**
 * Store for Opportunity Offers.
 */
export class OpportunityOfferStore {
  /**
   * The Opportunity Offers collection.
   */
  readonly opportunityOffers = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new OpportunityOffer(),
  })

  /**
   * Emits pending offers. Used on first load.
   */
  private readonly _offersFromFetch$ = defer(async () => {
    const request = new ListOpportunityOffersRequest().setPageSize(100)
    return this._api.opportunities.listOpportunityOffers(request)
  }).pipe(
    mergeMap((resp) => resp.getOffersList()),
    map((x) => x.toObject()),
    // Realtime still brings in new offers when the first load fails.
    catchError(() => EMPTY),
  )

  /**
   * Observable of incoming offers.
   */
  readonly incomingOffers$

  /**
   * Observable of removed offers.
   */
  readonly offersRemoved$

  constructor(
    private readonly _api: OpportunitiesApi,
    private readonly _acceptOffer: AcceptOfferFn,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _canViewOpportunities$: Observable<boolean>,
  ) {
    this.incomingOffers$ = merge(
      this._offersFromFetch$,
      this._api.realtime$.pipe(
        filter((x) => x.getTypeCase() === TypeCase.OPPORTUNITY_OFFERED),
        map((x) => x.getOpportunityOffered()!.toObject()),
        filterNonNil(),
      ),
    ).pipe(
      whileActive(this._canViewOpportunities$),
      filter((x) => !this.opportunityOffers.get(x.id)),
      map((x) =>
        this.opportunityOffers.set(this._mapOpportunityOfferProtoToProps(x)),
      ),
      share(),
    )

    this.offersRemoved$ = this._canViewOpportunities$.pipe(
      find(Boolean),
      switchMap(() => this.opportunityOffers.removals$),
    )

    this._api.realtime$
      .pipe(
        whileActive(this._canViewOpportunities$),
        filter((x) => x.getTypeCase() === TypeCase.OPPORTUNITY_OFFER_REMOVED),
        map((x) => x.getOpportunityOfferRemoved()!.toObject()),
        filterNonNil(),
      )
      .subscribe((x) => this.opportunityOffers.remove(x.id))
  }

  /**
   * Fetches a singular opportunity offer.
   */
  async fetchOpportunityOffer(offerId: Id): Promise<OpportunityOffer> {
    const query = new GetOpportunityOfferRequest().setId(Id.assert(offerId))

    return this._api.opportunities
      .getOpportunityOffer(query)
      .then((res) =>
        this.opportunityOffers.set(
          this._mapOpportunityOfferProtoToProps(res.toObject()),
        ),
      )
  }

  /**
   * Accepts the given opportunity.
   */
  async acceptOffer(opportunityId: Id) {
    const id = Id.assert(opportunityId)

    return this._acceptOffer(id).then(() => this.opportunityOffers.remove(id))
  }

  /**
   * Declines the given opportunity.
   */
  async declineOffer(opportunityId: Id) {
    const id = Id.assert(opportunityId)

    return this._api.opportunities
      .declineOpportunityOffer(new DeclineOpportunityOfferRequest().setId(id))
      .then(() => this.opportunityOffers.remove(id))
  }

  private _mapOpportunityOfferProtoToProps(
    proto: OpportunityOfferProto.AsObject,
  ): Partial<OpportunityOfferProps> {
    return {
      id: proto.id,
      workspace: this._primeWorkspace({ id: proto.workspaceId }),
      name: proto.name,
      description: proto.description,
      legendName: proto.legendName,
      clientUser: proto.client
        ? this._primeUser({
            publicId: proto.client.userPublicId,
            givenName: proto.client.givenName?.value,
            familyName: proto.client.familyName?.value,
            picture: proto.client.picture?.value,
          })
        : undefined,
      specs: proto.specsList,
      providerCutAmount: maybeDecimalToNumber(proto.providerCutAmount),
      dateOffered: timestampToDate(proto.dateOffered),
      dateExpires: timestampToDate(proto.dateExpires),
      expirationAction: proto.expirationAction
        ? mapExpirationActionProtoToOfferExpirationAction(
            proto.expirationAction,
          )
        : undefined,
      targeted: proto.targeted,
      consultation: proto.consultation
        ? mapOpportunityOfferConsultationProtoToConsultation(proto.consultation)
        : undefined,
    }
  }
}
